if (typeof config === 'undefined') {
  var config = {
    isTouch: ('ontouchstart' in window) || navigator.maxTouchPoints > 0,
    motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  };
}

const appConfig = {
  get isTouch() {
    return window.theme?.config?.isTouch ?? config.isTouch;
  },
  get motionReduced() {
    return window.theme?.config?.motionReduced ?? config.motionReduced;
  },
};

const NAV_EASING = [0.7, 0, 0.2, 1];
const MEGA_EASING = [0.39, 0.575, 0.565, 1];

function dropdownDuration() {
  // return appConfig.motionReduced ? 0 : 0.6;
  return appConfig.motionReduced ? 0 : 0.3;
}

function dropdownHideOpacityDuration() {
  return appConfig.motionReduced ? 0 : 0.3;
}

function megaDuration() {
  // return appConfig.motionReduced ? 0 : 0.5;
  return appConfig.motionReduced ? 0 : 0.3;
}

function getDropdownSlideOffset(level) {
  return level === 'top' ? '-105%' : '2rem';
}

const clearDropdownCount = new Map();

class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown-menu',
    };

    this.events = {
      afterHide: 'menu:afterHide',
      afterShow: 'menu:afterShow',
    };

    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute('open');
    this.transitionToken = 0;
    this._animations = new Set();
    this._contentOpenTimer = null;
    this.hoverTimeout = null;
    this._pointerInside = false;

    this.summaryElement.addEventListener('click', this.handleSummaryClick.bind(this));

    if (this.trigger === 'hover') {
      this.summaryElement.addEventListener('focusin', (event) => {
        if (event.target !== this.summaryElement) return;
        // Escape restores focus to this summary — do not reopen.
        if (this._ignoreNextSummaryFocus) {
          this._ignoreNextSummaryFocus = false;
          return;
        }
        this.open = true;
      });
      this.summaryElement.addEventListener('focusout', (event) => {
        if (!this.contentElement.contains(event.relatedTarget)) this.open = false;
      });
    }

    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener);
    this.addEventListener('mouseleave', this.detectHoverListener);

    this.summaryElement.setAttribute('aria-expanded', 'false');
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
  }

  get open() {
    return this._open;
  }

  get trigger() {
    if (appConfig.isTouch) return 'click';
    return this.getAttribute('trigger') || 'click';
  }

  get level() {
    return this.hasAttribute('level') ? this.getAttribute('level') : 'top';
  }

  get hoverDelay() {
    const delay = Number(this.getAttribute('hover-delay'));
    return Number.isFinite(delay) && delay >= 0 ? delay : 150;
  }

  get animationPanel() {
    return this.contentElement.firstElementChild;
  }

  cancelAnimations() {
    this._animations.forEach((animation) => animation.cancel?.());
    this._animations.clear();
  }

  trackAnimation(animation) {
    this._animations.add(animation);
    animation.finished.then(
      () => this._animations.delete(animation),
      () => this._animations.delete(animation),
    );
    return animation;
  }

  resetClosedVisuals() {
    this.cancelAnimations();
    this.contentElement?.style.removeProperty('opacity');
    this.contentElement?.style.removeProperty('visibility');
    this.animationPanel?.style.removeProperty('transform');
    this.animationPanel?.style.removeProperty('visibility');
  }

  handleSummaryClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const dataLink = this.summaryElement.getAttribute('data-link') || '';

    // Hover trigger: click navigates via data-link (top + child). Child is always hover.
    if (!appConfig.isTouch && this.trigger === 'hover' && dataLink) {
      window.location.href = dataLink;
      return;
    }

    this.open = !this.open;
  }

  closeSiblingMenus() {
    if (this.level === 'top') {
      document
        .querySelectorAll('details[is="details-dropdown"][level="top"], details[is="details-mega"]')
        .forEach((details) => {
          if (details !== this && details.open) {
            details.open = false;
          }
        });
      return;
    }

    const parentMenu = this.parentElement?.closest('details[is="details-dropdown"]');
    if (!parentMenu) return;

    parentMenu.querySelectorAll('details[is="details-dropdown"][level="child"]').forEach((details) => {
      if (details !== this && details.open) {
        details.open = false;
      }
    });
  }

  async transition(value) {
    const token = ++this.transitionToken;
    clearTimeout(this._contentOpenTimer);
    this._contentOpenTimer = null;

    if (value) {
      this.closeSiblingMenus();
      clearDropdownCount.set(DetailsDropdown, (clearDropdownCount.get(DetailsDropdown) || 0) + 1);

      if (document.body.classList.contains('has-mega-menu')) {
        document.body.classList.remove('has-mega-menu');
      }

      document.body.classList.add(this.classes.bodyClass);
      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      this.summaryElement.setAttribute('aria-expanded', 'true');
      this._contentOpenTimer = setTimeout(() => {
        this._contentOpenTimer = null;
        if (this._open && token === this.transitionToken) {
          this.contentElement.setAttribute('open', '');
        }
      }, 100);
      document.addEventListener('click', this.handleOutsideClick);
      document.addEventListener('keydown', this.handleEscKeyPress);
      document.addEventListener('focusout', this.handleFocusOut);

      await this.showWithTransition();
      if (token !== this.transitionToken || !this._open) return;

      this.needsReverse();
      this.dispatchEvent(new CustomEvent(this.events.afterShow));
      return;
    }

    clearDropdownCount.set(DetailsDropdown, (clearDropdownCount.get(DetailsDropdown) || 1) - 1);
    document.body.classList.toggle(this.classes.bodyClass, clearDropdownCount.get(DetailsDropdown) > 0);

    this.summaryElement.removeAttribute('open');
    this.summaryElement.setAttribute('aria-expanded', 'false');
    this.contentElement.removeAttribute('open');
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleEscKeyPress);
    document.removeEventListener('focusout', this.handleFocusOut);

    await this.hideWithTransition();
    if (token !== this.transitionToken) return;

    this.resetClosedVisuals();
    if (!this._open) this.removeAttribute('open');
    this.dispatchEvent(new CustomEvent(this.events.afterHide));
  }

  async showWithTransition() {
    this.cancelAnimations();

    const duration = dropdownDuration();
    const panel = this.animationPanel;
    const translateY = getDropdownSlideOffset(this.level);

    this.trackAnimation(
      Motion.animate(
        this.contentElement,
        { opacity: [0, 1], visibility: 'visible' },
        {
          duration,
          easing: NAV_EASING,
          delay: appConfig.motionReduced ? 0 : 0.1,
        },
      ),
    );

    const panelAnimation = this.trackAnimation(
      Motion.animate(
        panel,
        { transform: [`translateY(${translateY})`, 'translateY(0)'] },
        { duration, easing: NAV_EASING },
      ),
    );

    return panelAnimation.finished.catch(() => {});
  }

  async hideWithTransition() {
    this.cancelAnimations();

    const duration = dropdownDuration();
    const opacityDuration = dropdownHideOpacityDuration();
    const panel = this.animationPanel;
    const translateY = getDropdownSlideOffset(this.level);

    const opacityAnimation = this.trackAnimation(
      Motion.animate(
        this.contentElement,
        { opacity: 0, visibility: 'hidden' },
        { duration: opacityDuration, easing: NAV_EASING },
      ),
    );

    const panelAnimation = this.trackAnimation(
      Motion.animate(
        panel,
        { transform: [`translateY(0)`, `translateY(${translateY})`] },
        { duration, easing: NAV_EASING },
      ),
    );

    await Promise.all([
      opacityAnimation.finished.catch(() => {}),
      panelAnimation.finished.catch(() => {}),
    ]);
  }

  handleOutsideClick(event) {
    const isClickInside = this.contains(event.target);
    const isClickOnDetailsDropdown = event.target.closest('details') instanceof DetailsDropdown;

    if (!isClickInside && !isClickOnDetailsDropdown && this.open) {
      this.open = false;
    }
  }

  handleEscKeyPress(event) {
    if (event.code !== 'Escape') return;

    // Only the innermost open menu that contains focus should handle Escape.
    const targetMenu = event.target.closest('details[open]');
    if (targetMenu !== this) return;

    event.preventDefault();
    this.open = false;
    this._ignoreNextSummaryFocus = true;
    this.summaryElement?.focus();
  }

  handleFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger !== 'hover') return;

    clearTimeout(this.hoverTimeout);
    this.hoverTimeout = null;

    if (event.type === 'mouseenter') {
      this._pointerInside = true;
      if (this.open) return;

      // Intentional dwell before open — skips fast header skims.
      this.hoverTimeout = setTimeout(() => {
        this.hoverTimeout = null;
        if (this._pointerInside) this.open = true;
      }, this.hoverDelay);
      return;
    }

    this._pointerInside = false;
    // Short close delay bridges adjacent menu items without flicker.
    this.hoverTimeout = setTimeout(() => {
      this.hoverTimeout = null;
      if (!this._pointerInside) this.open = false;
    }, this.hoverDelay);
  }

  needsReverse() {
    if (this.contentElement.offsetLeft + this.contentElement.clientWidth * 2 > window.innerWidth) {
      this.contentElement.classList.add('should-reverse');
    }
  }
}
customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
clearDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    this.classes.bodyClass = 'has-mega-menu';

    // Product mega menu = swiper → product-card → secondary-media (nested swiper).
    // Init secondary only after panel is open so parent slide widths exist.
    this.addEventListener('menu:afterShow', () => {
      requestAnimationFrame(() => {
        this.querySelectorAll('.swiper').forEach((el) => {
          const instance = el.__cocoSwiperInstance || el.swiper;
          if (!instance || instance.destroyed) return;
          instance.updateSize?.();
          instance.updateSlides?.();
          instance.update?.();
        });
        this.querySelectorAll('secondary-media').forEach((el) => {
          el.ensureInit?.();
        });
      });
    });

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        this.open = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        this.open = false;
      });
    }
  }

  async showWithTransition() {
    this.cancelAnimations();

    const duration = megaDuration();
    const panel = this.animationPanel;

    const panelAnimation = this.trackAnimation(
      Motion.animate(
        panel,
        {
          visibility: 'visible',
          transform: ['translateY(-100%)', 'translateY(0)'],
        },
        { duration, easing: MEGA_EASING },
      ),
    );

    return panelAnimation.finished.catch(() => {});
  }

  async hideWithTransition() {
    this.cancelAnimations();

    const duration = megaDuration();
    const panel = this.animationPanel;

    const panelAnimation = this.trackAnimation(
      Motion.animate(
        panel,
        {
          visibility: 'hidden',
          transform: ['translateY(0)', 'translateY(-100%)'],
        },
        { duration, easing: MEGA_EASING },
      ),
    );

    return panelAnimation.finished.catch(() => {});
  }
}
customElements.define('details-mega', DetailsMega, { extends: 'details' });
